"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { Camera, Loader2, Upload, User, X } from "lucide-react";
import * as React from "react";
import type { UploadHandler } from "../lib/types";
import { cn, formatBytes, isImageFile } from "../lib/utils";

const avatarVariants = cva(
  "relative rounded-full overflow-hidden bg-muted flex items-center justify-center cursor-pointer transition-all group",
  {
    variants: {
      size: {
        sm: "h-16 w-16",
        default: "h-24 w-24",
        lg: "h-32 w-32",
        xl: "h-40 w-40",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

const iconVariants = cva("text-muted-foreground", {
  variants: {
    size: {
      sm: "h-6 w-6",
      default: "h-8 w-8",
      lg: "h-10 w-10",
      xl: "h-12 w-12",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const overlayVariants = cva(
  "absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity",
  {
    variants: {
      size: {
        sm: "",
        default: "",
        lg: "",
        xl: "",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

export interface AvatarUploadProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">,
    VariantProps<typeof avatarVariants> {
  /**
   * Upload handler configuration
   */
  handler: UploadHandler;
  /**
   * Current avatar URL
   */
  value?: string | null;
  /**
   * Called when avatar changes
   */
  onChange?: (url: string | null) => void;
  /**
   * Called when upload completes
   */
  onUploadComplete?: (url: string) => void;
  /**
   * Maximum file size in bytes
   * @default 5 * 1024 * 1024 (5MB)
   */
  maxSize?: number;
  /**
   * Accepted image types
   * @default ["image/jpeg", "image/png", "image/gif", "image/webp"]
   */
  accept?: string[];
  /**
   * Whether to allow removing the avatar
   * @default true
   */
  allowRemove?: boolean;
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Fallback initials or name
   */
  fallback?: string;
}

type UploadState = "idle" | "uploading" | "processing" | "error";

export function AvatarUpload({
  handler,
  value,
  onChange,
  onUploadComplete,
  maxSize = 5 * 1024 * 1024,
  accept = ["image/jpeg", "image/png", "image/gif", "image/webp"],
  allowRemove = true,
  disabled = false,
  fallback,
  size,
  className,
  ...props
}: AvatarUploadProps) {
  const [uploadState, setUploadState] = React.useState<UploadState>("idle");
  const [error, setError] = React.useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Clean up preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleClick = React.useCallback(() => {
    if (!disabled && uploadState === "idle") {
      inputRef.current?.click();
    }
  }, [disabled, uploadState]);

  const handleRemove = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      onChange?.(null);
    },
    [onChange, previewUrl],
  );

  const handleFileSelect = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";

      if (!file) return;

      // Validate file type
      if (!isImageFile(file.type) || !accept.includes(file.type)) {
        setError("Please select a valid image file");
        return;
      }

      // Validate file size
      if (file.size > maxSize) {
        setError(`Image must be less than ${formatBytes(maxSize)}`);
        return;
      }

      setError(null);

      // Create preview
      const objectUrl = URL.createObjectURL(file);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(objectUrl);

      try {
        setUploadState("uploading");

        // Get upload URL
        const { uploadUrl, assetId } = await handler.getUploadUrl(file);

        // Upload file
        const response = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        if (!response.ok) {
          throw new Error("Upload failed");
        }

        setUploadState("processing");

        // Confirm upload
        let finalUrl = objectUrl;
        if (handler.onUploadComplete && assetId) {
          const result = await handler.onUploadComplete(assetId, file);
          if (result.url) {
            finalUrl = result.url;
            // Clean up object URL since we have the real URL now
            URL.revokeObjectURL(objectUrl);
            setPreviewUrl(null);
          }
        }

        setUploadState("idle");
        onChange?.(finalUrl);
        onUploadComplete?.(finalUrl);
      } catch (err) {
        setUploadState("error");
        setError(err instanceof Error ? err.message : "Upload failed");
        handler.onError?.(err instanceof Error ? err : new Error("Upload failed"), file);
      }
    },
    [accept, handler, maxSize, onChange, onUploadComplete, previewUrl],
  );

  const displayUrl = previewUrl || value;
  const isLoading = uploadState === "uploading" || uploadState === "processing";

  return (
    <div className={cn("flex flex-col items-center gap-2", className)} {...props}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleClick();
          }
        }}
        className={cn(
          avatarVariants({ size }),
          disabled && "opacity-50 cursor-not-allowed",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept.join(",")}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || isLoading}
        />

        {/* Avatar image or fallback */}
        {displayUrl ? (
          <img src={displayUrl} alt="Avatar" className="h-full w-full object-cover" />
        ) : fallback ? (
          <span
            className={cn(
              "font-medium text-muted-foreground uppercase",
              size === "sm" && "text-lg",
              size === "default" && "text-xl",
              size === "lg" && "text-2xl",
              size === "xl" && "text-3xl",
            )}
          >
            {fallback.slice(0, 2)}
          </span>
        ) : (
          <User className={cn(iconVariants({ size }))} />
        )}

        {/* Hover overlay */}
        {!disabled && !isLoading && (
          <div className={cn(overlayVariants({ size }))}>
            <Camera className="h-6 w-6 text-white" />
          </div>
        )}

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          </div>
        )}

        {/* Remove button */}
        {allowRemove && displayUrl && !disabled && !isLoading && (
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Helper text */}
      <div className="text-center">
        {error ? (
          <p className="text-xs text-destructive">{error}</p>
        ) : isLoading ? (
          <p className="text-xs text-muted-foreground">
            {uploadState === "uploading" ? "Uploading..." : "Processing..."}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">Click to {displayUrl ? "change" : "upload"}</p>
        )}
      </div>
    </div>
  );
}

AvatarUpload.displayName = "AvatarUpload";
